package com.veldekar.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.title
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.icons.ArrowDownIcon
import com.varabyte.kobweb.silk.components.icons.ArrowUpIcon
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.veldekar.HeadlineTextStyle
import com.veldekar.UncoloredButtonVariant
import com.veldekar.components.layouts.PageLayoutData
import com.veldekar.components.log.SessionListLinks
import com.veldekar.toSitePalette
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

// Container that has a tagline and grid on desktop, and just the tagline on mobile
val HeroContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(2.cssRem) }
    Breakpoint.MD { Modifier.margin { top(20.vh) } }
}

// Section header row above session list
val SectionHeaderStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .margin(bottom = 1.5.cssRem)
}

// Subtle pill button variant for sort order toggle
val SortButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .gap(0.5.cssRem)
            .padding(leftRight = 0.7.cssRem, topBottom = 0.35.cssRem)
            .borderRadius(50.px)
            .fontSize(0.8.cssRem)
            .opacity(0.5f)
            .cursor(Pointer)
            .transition(
                Transition.of("background-color", 0.2.s),
                Transition.of("opacity", 0.2.s)
            )
    }
    hover {
        Modifier
            .opacity(1f)
            .backgroundColor(colorMode.toSitePalette().nearBackground)
    }
}

// A demo grid that appears on the homepage because it looks good
val HomeGridStyle = CssStyle.base {
    Modifier
        .gap(0.5.cssRem)
        .width(70.cssRem)
        .height(18.cssRem)
}

private val GridCellColorVar by StyleVariable<Color>()
val HomeGridCellStyle = CssStyle.base {
    Modifier
        .backgroundColor(GridCellColorVar.value())
        .boxShadow(blurRadius = 0.6.cssRem, color = GridCellColorVar.value())
        .borderRadius(1.cssRem)
}

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Home",
            ogTitle = "Veldekar - Campaign Session Log",
            ogDescription = "Chronicles, session logs, interactive world map, and lore from the Veldekar D&D campaign.",
            ogImage = "ai/home_preview.webp",
            ogImageWidth = 1200,
            ogImageHeight = 630,
            ogImageAlt = "The heroes of Veldekar standing ready for adventure on an overlook overlooking the realm",
        )
    )
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    var reverseChronological by remember { mutableStateOf(true) }

    Div(Modifier.fillMaxWidth().padding(leftRight = 2.cssRem, bottom = 2.cssRem).toAttrs()) {
        H1(HeadlineTextStyle.toModifier().toAttrs()) { Text("Session Log") }
        Row(
            SectionHeaderStyle.toModifier(),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Button(
                onClick = { reverseChronological = !reverseChronological },
                modifier = Modifier.title(if (reverseChronological) "Showing newest first" else "Showing oldest first"),
                variant = UncoloredButtonVariant.then(SortButtonVariant),
            ) {
                Text(if (reverseChronological) "Newest first" else "Oldest first")
                if (reverseChronological) {
                    ArrowDownIcon(Modifier.ariaHidden(true))
                } else {
                    ArrowUpIcon(Modifier.ariaHidden(true))
                }
            }
        }
        SessionListLinks(reverseChronological)
    }
}
